import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { retry, timer } from 'rxjs';
import { isChunkLoadError } from './chunk-load-error';

/**
 * Messages browsers use when a request never reaches the server (offline,
 * dropped connection, DNS failure, connection reset…):
 * - Chrome / Edge: "Failed to fetch"
 * - Firefox: "NetworkError when attempting to fetch resource."
 * - Safari: "Load failed"
 * - XMLHttpRequest polyfills (e.g. pdfmake): "Network request failed"
 */
const NETWORK_ERROR_MESSAGE = /Failed to fetch|NetworkError|Load failed|Network request failed/i;

const IDEMPOTENT_METHODS = ['GET', 'HEAD', 'OPTIONS'];

/**
 * Whether the error is due to the network (the server could not be reached)
 * rather than an error returned by the server.
 */
export function isNetworkError(error: unknown): boolean {
  if (error instanceof HttpErrorResponse) {
    return error.status === 0;
  }

  return (
    error instanceof Error && !isChunkLoadError(error) && NETWORK_ERROR_MESSAGE.test(error.message)
  );
}

/**
 * Retries idempotent requests that failed because of the network, to smooth
 * over brief connection drops. Server errors are never retried.
 */
export function networkRetryInterceptor({
  count = 2,
  delay = 1000,
}: { count?: number; delay?: number } = {}): HttpInterceptorFn {
  return (req, next) => {
    if (!IDEMPOTENT_METHODS.includes(req.method)) {
      return next(req);
    }

    return next(req).pipe(
      retry({
        count,
        delay: (error: unknown, retryCount) => {
          if (!isNetworkError(error)) {
            throw error;
          }

          return timer(delay * retryCount);
        },
      }),
    );
  };
}
